<template>
    <vx-expandable-card>
        <template v-slot:header> What </template>
        <template v-slot:content>
            <vx-alert v-if="displayFirstError(validation.messages.$errors)" color="danger"
                :closeable="false"
                class="text-xs text-rose-800 mt-4"
                aria-invalid="true">
                    <template v-slot:icon>
                        <font-awesome-icon :icon="faTriangleExclamation">
                        </font-awesome-icon>
                    </template>
                {{ displayFirstError(validation.messages.$errors) }}
            </vx-alert>
            <div class="flex flex-col mt-4 gap-8">
                <campaign-step-form-message v-for="(message, index) in props.modelValue"
                    :key="'campaign_message.' + message.id"
                    :campaignId="props.campaignId"
                    ref="messagesRef"
                    :modelValue="message"
                    @update:modelValue="updateModelValue(index, $event)"
                    :index="index"
                    @remove="removeMessage(index)"
                    :total="props.modelValue.length"></campaign-step-form-message>

                <div v-if="props.modelValue.length < 20"
                    class="rounded-lg bg-slate-100 px-8 py-4 flex justify-between items-center text-slate-700">
                    Add New Message

                    <vx-button @click.prevent="addMessage()"
                        type="button"
                        size="sm"
                        color="muted">
                        Add <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxExpandableCard, VxButton, VxAlert } from '@voxie/frontend-components';
import CampaignStepFormMessage from './CampaignStepFormMessage.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCirclePlus, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { randomString } from '~/utils/string';
import { computed, nextTick, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';

const props = defineProps({
    campaignId: {
        type: [Number, undefined],
        required: false,
    },
    modelValue: {
        type: Array,
        required: true,
        default: () => ([]),
    },
});

const messagesRef = ref([]);

const emit = defineEmits(["update:modelValue"]);

const atLeastOnePublished = helpers.withMessage(
    'At least one message must be active.',
    (value) => {
        if (!Array.isArray(value) || value.length === 0) {
            return false;
        }
        return value.some(message => message.status === 'published');
    }
);

const validation = useVuelidate({
    messages: { atLeastOnePublished },
}, computed(() => ({ messages: props.modelValue })));

const updateModelValue = (updateIndex, $event) => {
    emit('update:modelValue', props.modelValue.map((message, index) => ({
        ...(updateIndex === index ? $event : message)
    })))
}

const addMessage = async () => {
    messagesRef.value?.forEach(message => message.close());

    emit('update:modelValue', [...props.modelValue, {
        id: randomString(),
        body: '',
        media_content_type: '',
        media_url: '',
        status: 'published',
        delay_type: 'minutes',
        delay_amount: 0,
        should_send_on_schedule: false,
        send_on_0: true,
        send_on_1: true,
        send_on_2: true,
        send_on_3: true,
        send_on_4: true,
        send_on_5: true,
        send_on_6: true,
        send_at_time: '09:00:00',
        send_stop_time: '20:00:00',
    }]);

    await nextTick();
    if (props.modelValue.length > 1) {
        messagesRef.value?.[props.modelValue.length - 1]?.focusMessage?.();
    }
}

watch(() => props.modelValue.length, () => {
    if (!props.modelValue.length) {
        addMessage();
    }
}, {
    immediate: true,
});

const removeMessage = (removeIndex) => {
    emit('update:modelValue', props.modelValue.filter((message, index) => index !== removeIndex));
}
</script>
